import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Card, Field, InfoTip, Loading, inputClass } from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

interface BookValueForecast {
  activeMembers: number;
  payingMembers: number;
  monthlyRunRate: number;
  annualRecurring: number;
  attritionAdjustedAnnual: number;
  monthlyAttritionPct: number;
  avgMonthlyPerMember: number;
  byState: { state: string; members: number; monthly: number; annual: number }[];
}
interface BookValueResponse {
  period: string | null;
  carrierId: string;
  forecast: BookValueForecast | null;
}

interface CohortRow {
  cohort: number;
  size: number;
  retained: number;
  retainedPct: number;
  lapsed: number;
  oepLapsed: number;
}
interface CohortRetention {
  cohorts: CohortRow[];
  totalMembers: number;
  evaluated: number;
  overallRetainedPct: number;
  oepBleed: { lapsed: number; oepLapsed: number; oepSharePct: number };
}
interface CohortResponse {
  period: string | null;
  carrierId: string;
  cohorts: CohortRetention | null;
}

interface Rollup {
  totalAnnual: number;
  totalAdjustedAnnual: number;
  totalActiveMembers: number;
  carrierCount: number;
  byLine: { line: string; annualValue: number; activeMembers: number; carriers: number }[];
  byCarrier: {
    carrierId: string;
    carrierName: string | null;
    line: string;
    annualValue: number;
    adjustedAnnual: number | null;
    activeMembers: number;
    asOfPeriod: string | null;
  }[];
  monthly: { period: string; amount: number }[];
}
interface RollupResponse {
  rollup: Rollup;
  entitled: { aca: boolean; medicare: boolean };
}

/** Product-line code → human label for the whole-book breakdown. */
function lineLabel(line: string): string {
  if (line === 'medicare') return 'Medicare Advantage';
  if (line === 'ancillary') return 'Ancillary / Med Supp';
  if (line === 'major_medical') return 'Major medical / ACA';
  return line;
}

/**
 * ACA / major-medical book-value forecast — the recurring-commission counterpart to
 * the Medicare renewal forecast. ACA pays per member per month, so the book's annual
 * recurring income is each active member's monthly run-rate × 12, read from what the
 * carrier actually pays. Shows the gross run-rate and an attrition-adjusted figure —
 * the "what's my book worth" number.
 */
export function BookValue() {
  const { baseUrl, token } = useAuth();
  const [searchParams] = useSearchParams();
  const { carriers } = useCarriers();
  const [carrierId, setCarrierId] = useState(searchParams.get('carrierId') ?? '');
  const [workspaceId, setWorkspaceId] = useState(searchParams.get('workspaceId') ?? '');
  const [period, setPeriod] = useState('');
  const [data, setData] = useState<BookValueResponse | null>(null);
  const [cohort, setCohort] = useState<CohortRetention | null>(null);
  const [rollup, setRollup] = useState<Rollup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wholeBook = carrierId === '';

  // Whole-book rollup — the default view: every carrier valued by the correct engine
  // for its product line, combined into one total + per-line + per-carrier + cash curve.
  useEffect(() => {
    if (!wholeBook) {
      setRollup(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/book-value-rollup?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Failed (${r.status})`))))
      .then((b: RollupResponse) => active && setRollup(b.rollup))
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [baseUrl, token, workspaceId, wholeBook]);

  useEffect(() => {
    if (!carrierId) return;
    let active = true;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ carrierId });
    if (period) qs.set('period', period);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/aca-book-value?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Failed (${r.status})`))))
      .then((b: BookValueResponse) => {
        if (!active) return;
        setData(b);
        if (!period && b.period) setPeriod(b.period);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, period, workspaceId]);

  // Enrollment-cohort retention (same page, second report).
  useEffect(() => {
    if (!carrierId) return;
    let active = true;
    setCohort(null);
    const qs = new URLSearchParams({ carrierId });
    if (period) qs.set('period', period);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/reports/cohort-retention?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Failed (${r.status})`))))
      .then((b: CohortResponse) => active && setCohort(b.cohorts))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, period, workspaceId]);

  const f = data?.forecast ?? null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Book value &amp; retention</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500">
            What your book is worth — the annual recurring commission it generates, read from what
            the carrier actually pays each month — and how each enrollment class holds up over time
            (including the OEP disenroll bleed).
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="w-48">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">All carriers (whole book)</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!wholeBook && (
            <div className="w-48">
              <Field label="Period">
                <PeriodPicker value={period} onChange={setPeriod} placeholder="Latest period" />
              </Field>
            </div>
          )}
        </div>
      </div>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {wholeBook ? (
        loading && !rollup ? (
          <Loading label="Valuing the whole book…" />
        ) : (
          <WholeBookView rollup={rollup} />
        )
      ) : loading && !f ? (
        <Loading label="Valuing the book…" />
      ) : !f || f.payingMembers === 0 ? (
        <Card>
          <p className="py-8 text-center text-sm text-neutral-500">
            No paid members for {formatPeriodLong(data?.period ?? null)} — this carrier's latest
            statement may be empty or unprocessed.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              tone="emerald"
              label="Annual recurring"
              value={formatCurrency(f.annualRecurring)}
              sub={`if fully retained · ${f.payingMembers.toLocaleString()} paying members`}
            />
            <StatCard
              tone="neutral"
              label="Attrition-adjusted"
              value={formatCurrency(f.attritionAdjustedAnnual)}
              sub={`at ${f.monthlyAttritionPct}%/mo observed churn`}
            />
            <StatCard
              tone="neutral"
              label="Monthly run-rate"
              value={formatCurrency(f.monthlyRunRate)}
              sub={`this month · ${formatPeriodLong(data?.period ?? null)}`}
            />
            <StatCard
              tone="neutral"
              label="Avg / member / mo"
              value={formatCurrency(f.avgMonthlyPerMember)}
              sub={`${f.activeMembers.toLocaleString()} members on the book`}
            />
          </div>

          <p className="text-xs text-neutral-500">
            Recurring value is built from members the carrier actually paid this period (a $0 or
            clawback month isn't counted). Attrition-adjusted decays the book at its observed
            month-over-month churn. Point this at a complete period — a partial statement
            understates the book.
          </p>

          <Card className="p-0">
            <div className="grid grid-cols-[1.4fr_0.7fr_1fr_1fr] gap-3 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
              <span>By state</span>
              <span className="text-right">Members</span>
              <span className="text-right">Monthly</span>
              <span className="text-right">Annual</span>
            </div>
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {f.byState.slice(0, 20).map((s) => (
                <div
                  key={s.state}
                  className="grid grid-cols-[1.4fr_0.7fr_1fr_1fr] items-center gap-3 px-5 py-2.5 text-sm"
                >
                  <span className="font-medium">{s.state}</span>
                  <span className="text-right text-neutral-600 dark:text-neutral-300">
                    {s.members.toLocaleString()}
                  </span>
                  <span className="text-right text-neutral-600 dark:text-neutral-300">
                    {formatCurrency(s.monthly)}
                  </span>
                  <span className="text-right font-medium">{formatCurrency(s.annual)}</span>
                </div>
              ))}
            </div>
          </Card>

          {cohort && cohort.evaluated > 0 && (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                  tone="emerald"
                  label="Overall retention"
                  value={`${cohort.overallRetainedPct}%`}
                  sub={`${cohort.evaluated.toLocaleString()} members with a known enrollment year`}
                />
                <StatCard
                  tone="neutral"
                  label="Lapsed"
                  value={cohort.oepBleed.lapsed.toLocaleString()}
                  sub="members who dropped off the book"
                />
                <StatCard
                  tone="neutral"
                  label="OEP bleed"
                  value={`${cohort.oepBleed.oepSharePct}%`}
                  sub={`${cohort.oepBleed.oepLapsed.toLocaleString()} lapses fell in the Jan–Mar window`}
                />
              </div>
              <Card className="p-0">
                <div className="grid grid-cols-[1fr_0.7fr_0.7fr_0.8fr_0.8fr] gap-3 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
                  <span>Enrollment class</span>
                  <span className="text-right">Members</span>
                  <span className="text-right">Retained</span>
                  <span className="text-right">Retention</span>
                  <span className="text-right">OEP lapses</span>
                </div>
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {cohort.cohorts.map((c) => (
                    <div
                      key={c.cohort}
                      className="grid grid-cols-[1fr_0.7fr_0.7fr_0.8fr_0.8fr] items-center gap-3 px-5 py-2.5 text-sm"
                    >
                      <span className="font-medium">{c.cohort} class</span>
                      <span className="text-right text-neutral-600 dark:text-neutral-300">
                        {c.size.toLocaleString()}
                      </span>
                      <span className="text-right text-neutral-600 dark:text-neutral-300">
                        {c.retained.toLocaleString()}
                      </span>
                      <span
                        className={`text-right font-medium ${c.retainedPct >= 70 ? 'text-emerald-600 dark:text-emerald-400' : c.retainedPct < 40 ? 'text-rose-600 dark:text-rose-400' : ''}`}
                      >
                        {c.retainedPct}%
                      </span>
                      <span className="text-right text-neutral-600 dark:text-neutral-300">
                        {c.oepLapsed.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Whole-book rollup: one total across every carrier, per-line + per-carrier + cash curve. */
function WholeBookView({ rollup }: { rollup: Rollup | null }) {
  if (!rollup || rollup.carrierCount === 0)
    return (
      <Card>
        <p className="py-8 text-center text-sm text-neutral-500">
          No carriers with data yet — or the enabled features don't cover any of this account's
          product lines (Medicare needs the Medicare lifecycle feature; ACA / ancillary need Book
          value).
        </p>
      </Card>
    );
  const maxMonthly = Math.max(1, ...rollup.monthly.map((m) => m.amount));
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          tone="emerald"
          label="Whole-book annual recurring"
          value={formatCurrency(rollup.totalAnnual)}
          sub={`${rollup.carrierCount} carrier${rollup.carrierCount === 1 ? '' : 's'} · ${rollup.totalActiveMembers.toLocaleString()} members`}
        />
        <StatCard
          tone="neutral"
          label="Attrition-adjusted"
          value={formatCurrency(rollup.totalAdjustedAnnual)}
          sub="ACA decayed by churn; Medicare retention-assumed"
        />
        <StatCard
          tone="neutral"
          label="Active members"
          value={rollup.totalActiveMembers.toLocaleString()}
          sub="on the book across all lines"
        />
        <StatCard
          tone="neutral"
          label="Lines"
          value={String(rollup.byLine.length)}
          sub={rollup.byLine.map((l) => lineLabel(l.line)).join(' · ')}
        />
      </div>

      <p className="flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
        Each carrier is valued by the model correct for its product line — Medicare by annual
        anniversary renewal at CMS rates, ACA / ancillary by monthly run-rate × 12 — on its latest{' '}
        <strong>complete</strong> period.
        <InfoTip title="Valued on complete periods only">
          Book value is computed from each carrier's most recent <strong>complete</strong> statement
          period. If the latest month looks partial — a half-loaded statement with far fewer members
          than the recent norm — it's skipped, so a data gap can't understate your book. The{' '}
          <strong>As of</strong> column shows exactly which period each carrier was valued on. The
          annual total is comparable across lines, but the cash <em>timing</em> differs (Medicare is
          front-loaded to renewal anniversaries — see the curve below).
        </InfoTip>
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rollup.byLine.map((l) => (
          <Card key={l.line}>
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              {lineLabel(l.line)}
            </div>
            <div className="mt-1 text-2xl font-semibold">{formatCurrency(l.annualValue)}</div>
            <div className="mt-1 text-xs text-neutral-500">
              {l.carriers} carrier{l.carriers === 1 ? '' : 's'} · {l.activeMembers.toLocaleString()}{' '}
              members
            </div>
          </Card>
        ))}
      </div>

      {rollup.monthly.length > 0 && (
        <Card>
          <div className="mb-3 text-sm font-semibold">Projected monthly cash — next 12 months</div>
          <div className="space-y-1.5">
            {rollup.monthly.map((m) => (
              <div key={m.period} className="flex items-center gap-3 text-sm">
                <span className="w-16 shrink-0 text-neutral-500">{m.period}</span>
                <div className="h-4 flex-1 rounded bg-neutral-100 dark:bg-neutral-800">
                  <div
                    className="h-4 rounded bg-emerald-500"
                    style={{ width: `${(m.amount / maxMonthly) * 100}%` }}
                  />
                </div>
                <span className="w-24 shrink-0 text-right font-medium">
                  {formatCurrency(m.amount)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-0">
        <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_1fr] gap-3 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
          <span>Carrier</span>
          <span>Line</span>
          <span>As of</span>
          <span className="text-right">Members</span>
          <span className="text-right">Annual value</span>
        </div>
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {rollup.byCarrier.map((c) => (
            <div
              key={c.carrierId}
              className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_1fr] items-center gap-3 px-5 py-2.5 text-sm"
            >
              <span className="font-medium">{c.carrierName ?? c.carrierId.slice(0, 8)}</span>
              <span className="text-neutral-600 dark:text-neutral-300">{lineLabel(c.line)}</span>
              <span className="text-neutral-500">{c.asOfPeriod ?? '—'}</span>
              <span className="text-right text-neutral-600 dark:text-neutral-300">
                {c.activeMembers.toLocaleString()}
              </span>
              <span className="text-right font-medium">{formatCurrency(c.annualValue)}</span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function StatCard({
  tone,
  label,
  value,
  sub,
}: {
  tone: 'emerald' | 'neutral';
  label: string;
  value: string;
  sub: string;
}) {
  const valueClass =
    tone === 'emerald'
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-neutral-900 dark:text-neutral-100';
  return (
    <Card>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={`mt-1 text-3xl font-semibold ${valueClass}`}>{value}</div>
      <div className="mt-1 text-xs text-neutral-500">{sub}</div>
    </Card>
  );
}
